import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { FALLBACK_CONTENT, phoneHref, SERVICE_ICONS } from "@/lib/data";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyBar from "@/components/StickyBar";

export default function ServicePage() {
  const { slug } = useParams();
  const [content, setContent] = useState(FALLBACK_CONTENT);
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent((c) => ({ ...c, ...r.data }))).catch(() => {});
    api
      .get("/services")
      .then((r) => {
        const found = r.data.find((s) => s.slug === slug);
        if (found) setService(found);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (service) document.title = `${service.name} Cleaning South Yorkshire | SY Exterior Cleaning`;
  }, [service]);

  return (
    <div className="bg-[#070b12] min-h-screen text-[#f0f4f8]" data-testid="service-page">
      <Navbar content={content} />
      {notFound ? (
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <h1 className="font-display uppercase text-4xl text-white mb-4">Service Not Found</h1>
          <Link to="/" data-testid="service-not-found-home-link" className="text-[#1e9bff] font-mono-lbl text-sm uppercase tracking-wider hover:underline">
            ← Back to all services
          </Link>
        </div>
      ) : !service ? (
        <div className="max-w-4xl mx-auto px-4 py-32 text-center text-[#93b4d4] font-mono-lbl text-sm">Loading…</div>
      ) : (
        <>
          <section className="relative py-20 md:py-28 overflow-hidden border-b border-[#1e9bff]/15">
            <div
              className="absolute inset-0 opacity-20 sy-drift pointer-events-none"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 40%, rgba(30,155,255,0.3) 0%, transparent 50%), radial-gradient(circle at 75% 60%, rgba(30,155,255,0.15) 0%, transparent 50%)",
                backgroundSize: "140% 140%",
              }}
            />
            <div className="max-w-4xl mx-auto px-4 relative z-10">
              <Link to="/#services" data-testid="service-back-link" className="text-xs font-mono-lbl uppercase tracking-widest text-[#93b4d4] hover:text-white transition-colors">
                ← All Services
              </Link>
              <div className="w-14 h-14 rounded-xl bg-[#1e9bff]/10 border border-[#1e9bff]/40 flex items-center justify-center text-[#1e9bff] my-8">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={SERVICE_ICONS[service.icon] || SERVICE_ICONS.house} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h1 className="font-display uppercase text-white text-[clamp(36px,5vw,64px)] leading-[0.95] mb-4" data-testid="service-title">
                {service.name} <span className="text-[#1e9bff]">in South Yorkshire</span>
              </h1>
              <p className="font-script text-[#9fd2ff] text-2xl mb-6 -rotate-1 origin-left">{service.tagline}</p>
              <p className="text-sm md:text-base text-[#c8d9ea] max-w-2xl mb-10" data-testid="service-description">{service.description}</p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to={`/?service=${service.slug}#booking`}
                  data-testid="service-book-btn"
                  className="px-7 py-4 rounded-xl bg-[#1e9bff] text-black font-display text-lg uppercase tracking-wider hover:bg-[#6ec2ff] hover:shadow-[0_0_30px_rgba(30,155,255,0.6)] transition-colors"
                >
                  Book This Service
                </Link>
                <a
                  href={phoneHref(content.phone)}
                  data-testid="service-call-btn"
                  className="px-6 py-4 rounded-xl border border-white/20 bg-white/5 hover:bg-white/10 text-white font-mono-lbl text-xs uppercase tracking-widest transition-colors"
                >
                  Call {content.phone}
                </a>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-24 border-b border-[#1e9bff]/15">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="font-display uppercase text-white text-[clamp(26px,3vw,40px)] leading-none mb-10">
                What&rsquo;s Included
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(service.includes_list || []).map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: 16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="p-5 rounded-xl bg-[#0c1626] border border-white/10 flex items-start gap-3"
                    data-testid={`service-include-${i}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-[#1e9bff]/20 text-[#1e9bff] flex items-center justify-center font-bold shrink-0 text-sm">✓</div>
                    <p className="text-sm text-[#c8d9ea]">{item}</p>
                  </motion.div>
                ))}
              </div>
              <div className="mt-10 p-6 rounded-2xl bg-[#0b1322] border border-[#1e9bff]/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <span className="text-xs font-mono-lbl text-[#1e9bff] uppercase tracking-widest block mb-1">Free Quote</span>
                  <p className="text-white text-sm">Every job starts with a free, no-obligation quote.</p>
                </div>
                <Link
                  to={`/?service=${service.slug}#booking`}
                  data-testid="service-quote-btn"
                  className="px-6 py-3 rounded-xl bg-[#1e9bff] text-black font-display uppercase tracking-wider hover:bg-[#60baff] transition-colors shrink-0"
                >
                  Get My Quote
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
      <Footer content={content} />
      <StickyBar content={content} />
      <div className="h-[calc(4.25rem+env(safe-area-inset-bottom))] md:hidden" />
    </div>
  );
}
